/*
  Landing page runtime for the lp-* sections.

  Every lp-* section renders its own .lp wrapper and loads this file, so the
  guard below keeps one runtime per page. Sections never call each other: the
  quiz publishes its state here and every other section reads it, which is
  why a section can be added, removed or reordered in the theme editor
  without touching any code.

  Events on document, all prefixed lp:
    lp:results  { rec, answers, meta }  a recommendation is on screen
    lp:reset                            the quiz went back to question one
    lp:gate     { lead }                the email gate validated
    lp:track    { event, ... }          analytics hand-off. Nothing here sends
                                        data anywhere; wire a pixel to this.
*/
(function () {
  'use strict';

  if (window.LP) return;

  var DESIGN = !!(window.Shopify && window.Shopify.designMode);
  var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  var state = { rec: null, answers: {}, meta: null, gatePassed: false, config: null };
  var quiz = null;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent('lp:' + name, { detail: detail || {} }));
  }

  function on(name, fn) {
    document.addEventListener('lp:' + name, function (ev) { fn(ev.detail || {}); });
  }

  function track(event, data) {
    var detail = { event: event, page: state.config ? state.config.storageKey : null };
    for (var k in data) if (Object.prototype.hasOwnProperty.call(data, k)) detail[k] = data[k];
    emit('track', detail);
  }

  function store(kind, key, value) {
    try {
      var s = window[kind];
      if (value === undefined) return s.getItem(key);
      if (value === null) s.removeItem(key);
      else s.setItem(key, value);
    } catch (e) {
      /* Private mode or a full quota: the page still works for this view. */
    }
    return null;
  }

  function hasProduct(rec) { return !!(rec && rec.action === 'product' && rec.product); }

  /* UTMs from the ad click ride along on every link that leaves the page. */
  function withUtms(href) {
    if (!href) return href;
    var params = new URLSearchParams(window.location.search);
    var keep = [];
    params.forEach(function (v, k) { if (k.indexOf('utm_') === 0) keep.push([k, v]); });
    if (!keep.length) return href;
    var url = new URL(href, window.location.origin);
    keep.forEach(function (p) { if (!url.searchParams.has(p[0])) url.searchParams.set(p[0], p[1]); });
    return url.origin === window.location.origin ? url.pathname + url.search + url.hash : url.toString();
  }

  function land(el, smooth) {
    if (!el) return;
    if (el.scrollIntoView) {
      el.scrollIntoView({ block: 'start', behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' });
    }
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
  }

  /* ------------------------------------------------------------------
     Recommendation rules. Rules are the quiz section's result blocks, in
     block order: the first rule whose conditions all hold wins, and a rule
     with no conditions is the default.
     ------------------------------------------------------------------ */

  function ruleMatches(rule, answers) {
    for (var i = 0; i < rule.when.length; i++) {
      var c = rule.when[i];
      if (c.v.indexOf(answers[c.q]) === -1) return false;
    }
    return true;
  }

  function recommend(config, answers) {
    for (var i = 0; i < config.rules.length; i++) {
      var rule = config.rules[i];
      if (rule.action === 'product' && !rule.product) continue;
      if (ruleMatches(rule, answers)) return rule;
    }
    return null;
  }

  /* A browse rule ends the quiz as soon as its own questions are answered,
     so "Something else" on question one does not ask two more questions the
     page cannot use. Only rules ahead of the first undecidable rule count,
     which keeps block order as the priority. */
  function earlyExit(config, answers) {
    for (var i = 0; i < config.rules.length; i++) {
      var rule = config.rules[i];
      if (rule.action === 'product' && !rule.product) continue;
      var decidable = rule.when.length > 0 && rule.when.every(function (c) {
        return Object.prototype.hasOwnProperty.call(answers, c.q);
      });
      if (!decidable) return null;
      if (ruleMatches(rule, answers)) return rule.action === 'browse' ? rule : null;
    }
    return null;
  }

  /* ------------------------------------------------------------------
     The quiz. One question on screen, the option tap advances, Back
     restores the previous answer, answers survive a reload, the exiting
     panel is inert, and focus follows the live heading in both motion
     modes.
     ------------------------------------------------------------------ */

  var CHECK_SVG = '<svg class="lp-opt__check" width="20" height="20" viewBox="0 0 256 256" fill="none" stroke="currentColor" stroke-width="20" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><polyline points="216 72 104 184 48 128"/></svg>';

  function createQuiz(root, config) {
    var questions = config.questions;
    var total = questions.length;
    var stage = $('.lp-quiz__stage', root);
    var fill = $('.lp-progress__fill', root);
    var label = $('.lp-progress__label', root);
    var backBtn = $('.lp-back', root);
    var body = root.closest('[data-lp-quiz-body]') || root;
    var mapTpl = $('template[data-lp-map]', root);
    var key = config.storageKey;
    var gateKey = key + ':gate';
    var answers = {};
    var index = 0;
    var started = false;
    var completeFired = false;
    var resultsShown = false;
    var safety = null;

    try { answers = JSON.parse(store('sessionStorage', key) || '{}') || {}; } catch (e) { answers = {}; }

    function save() { store('sessionStorage', key, JSON.stringify(answers)); }
    function gatePassed() { return store('sessionStorage', gateKey) === '1'; }

    function answered() {
      var n = 0;
      questions.forEach(function (q) { if (Object.prototype.hasOwnProperty.call(answers, q.id)) n++; });
      return n;
    }

    function isComplete() { return answered() >= total; }

    function getAnswers() {
      var out = {};
      questions.forEach(function (q) {
        if (Object.prototype.hasOwnProperty.call(answers, q.id)) out[q.id] = answers[q.id];
      });
      return out;
    }

    function buildMap(q) {
      var frag = mapTpl.content.cloneNode(true);
      var regions = $$('[data-lp-region]', frag);
      regions.forEach(function (r) {
        var opt = q.options[parseInt(r.getAttribute('data-lp-region'), 10)];
        if (!opt) { r.parentNode.removeChild(r); return; }
        r.setAttribute('data-value', opt.value);
        r.setAttribute('aria-pressed', answers[q.id] === opt.value ? 'true' : 'false');
        $('.lp-region__label', r).textContent = opt.label;
      });
      return frag;
    }

    function buildPanel(i) {
      var q = questions[i];
      var panel = document.createElement('div');
      panel.className = 'lp-q';
      panel.setAttribute('data-index', String(i));
      var h = document.createElement('h3');
      h.className = 'lp-title lp-q__title';
      h.id = root.id + '-q' + i;
      h.setAttribute('tabindex', '-1');
      h.textContent = q.title;
      panel.appendChild(h);
      if (q.map && mapTpl) panel.appendChild(buildMap(q));
      var list = document.createElement('div');
      list.className = 'lp-opts';
      list.setAttribute('role', 'group');
      list.setAttribute('aria-labelledby', h.id);
      q.options.forEach(function (opt) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'lp-opt';
        b.setAttribute('data-value', opt.value);
        b.setAttribute('aria-pressed', answers[q.id] === opt.value ? 'true' : 'false');
        var span = document.createElement('span');
        span.textContent = opt.label;
        b.appendChild(span);
        b.insertAdjacentHTML('beforeend', CHECK_SVG);
        list.appendChild(b);
      });
      panel.appendChild(list);
      return panel;
    }

    function paintProgress(i) {
      if (label) label.textContent = config.progressLabel.replace('{n}', String(i + 1)).replace('{total}', String(total));
      if (fill) fill.style.width = Math.round(((i + 1) / total) * 100) + '%';
      if (backBtn) backBtn.hidden = i === 0;
    }

    function retire(node) {
      node.classList.add('lp-q--exit');
      node.setAttribute('aria-hidden', 'true');
      node.inert = true;
      node.setAttribute('inert', '');
      var gone = false;
      function done() {
        if (gone) return;
        gone = true;
        if (node.parentNode) node.parentNode.removeChild(node);
      }
      node.addEventListener('transitionend', done, { once: true });
      window.setTimeout(done, 600);
    }

    function armSafety() {
      if (safety) window.clearTimeout(safety);
      safety = window.setTimeout(function () {
        $$('.lp-q--exit', stage).forEach(function (n) { if (n.parentNode) n.parentNode.removeChild(n); });
        $$('.lp-q--in', stage).forEach(function (n) { n.classList.remove('lp-q--in'); });
      }, 1600);
    }

    function livePanel() { return $('.lp-q:not(.lp-q--exit)', stage); }

    function render(i, dir, carryFocus) {
      index = i;
      root.setAttribute('data-dir', dir === 'back' ? 'back' : 'forward');
      var old = livePanel();
      var hadFocus = !!(old && old.contains(document.activeElement));
      var next = buildPanel(i);
      if (reducedMotion()) {
        stage.innerHTML = '';
        stage.appendChild(next);
      } else {
        if (old) retire(old);
        next.classList.add('lp-q--in');
        stage.appendChild(next);
        void next.offsetWidth;
        next.classList.remove('lp-q--in');
      }
      paintProgress(i);
      if (hadFocus || carryFocus) {
        var h = $('.lp-q__title', next);
        if (h) h.focus({ preventScroll: true });
      }
      armSafety();
    }

    function showResults(source, rec) {
      var r = rec || recommend(config, getAnswers());
      resultsShown = true;
      body.hidden = !!(r && r.action === 'browse');
      publishResults(r, getAnswers(), { source: source, gatePassed: gatePassed() });
      return r;
    }

    function advanceFrom(i) {
      var exit = earlyExit(config, getAnswers());
      if (exit) { showResults('complete', exit); return; }
      if (i + 1 < total) { render(i + 1, 'forward', false); return; }
      if (!completeFired) { completeFired = true; track('QuizComplete', { answers: getAnswers() }); }
      showResults(resultsShown ? 'refresh' : 'complete');
    }

    function record(i, value) {
      var q = questions[i];
      if (!q || !q.options.some(function (o) { return o.value === value; })) return false;
      answers[q.id] = value;
      save();
      return true;
    }

    function markStart() {
      if (started) return false;
      started = true;
      track('ViewContent', { content_name: 'quiz_start' });
      return true;
    }

    stage.addEventListener('click', function (ev) {
      var btn = ev.target.closest ? ev.target.closest('.lp-opt, .lp-region') : null;
      if (!btn || btn.closest('.lp-q--exit')) return;
      var panel = btn.closest('.lp-q');
      if (!panel) return;
      var i = parseInt(panel.getAttribute('data-index'), 10);
      if (i !== index) return;
      var value = btn.getAttribute('data-value');
      if (!record(i, value)) return;
      markStart();
      $$('.lp-opt, .lp-region', panel).forEach(function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-value') === value ? 'true' : 'false');
      });
      advanceFrom(i);
    });

    if (backBtn) {
      backBtn.addEventListener('click', function () {
        if (index > 0) render(index - 1, 'back', true);
      });
    }

    return {
      root: root,
      markStart: markStart,
      getAnswers: getAnswers,
      isComplete: isComplete,
      gatePassed: gatePassed,
      start: function () {
        render(0, 'forward', false);
        var exit = earlyExit(config, getAnswers());
        if (exit) { showResults('resume', exit); return; }
        if (isComplete()) {
          completeFired = true;
          showResults('resume');
        }
      },
      /* Back from a browse outcome: the quiz comes back on the question
         that ended it, with that answer still selected. */
      reopen: function () {
        body.hidden = false;
        resultsShown = false;
        publishResults(null, getAnswers(), { source: 'reopen', gatePassed: gatePassed() });
        render(index, 'back', true);
      },
      submitGate: function (lead) {
        if (!isComplete() || gatePassed()) return false;
        store('sessionStorage', gateKey, '1');
        emit('gate', { lead: lead });
        showResults('gate');
        return true;
      },
      focusCurrent: function () {
        var h = $('.lp-q__title', livePanel() || stage);
        if (h) h.focus({ preventScroll: true });
      }
    };
  }

  function publishResults(rec, answers, meta) {
    state.rec = rec;
    state.answers = answers;
    state.meta = meta;
    state.gatePassed = !!meta.gatePassed;
    applyReveals();
    emit(rec ? 'results' : 'reset', { rec: rec, answers: answers, meta: meta });
  }

  /* ------------------------------------------------------------------
     Surfaces that wait for the quiz. data-lp-reveal="results" appears once
     a product is recommended, "gate" once the email gate has also passed.
     The theme editor shows them all so they can be edited.
     ------------------------------------------------------------------ */

  function applyReveals(scope) {
    $$('[data-lp-reveal]', scope).forEach(function (el) {
      var cond = el.getAttribute('data-lp-reveal');
      var ok = hasProduct(state.rec) && (cond === 'results' || (cond === 'gate' && state.gatePassed));
      el.hidden = !(ok || DESIGN);
    });
  }

  /* ------------------------------------------------------------------
     Result card. Written from the recommended rule's data, which Liquid
     rendered from the picked product: title, live price, rating, image.
     ------------------------------------------------------------------ */

  var BENEFIT_SVG = '<svg width="18" height="18" viewBox="0 0 256 256" fill="none" stroke="currentColor" stroke-width="20" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><polyline points="216 72 104 184 48 128"/></svg>';

  function setText(root, sel, text) {
    $$(sel, root).forEach(function (n) { n.textContent = text == null ? '' : text; });
  }

  function paintProduct(root, rec, answers) {
    var p = rec.product;
    setText(root, '[data-lp-name]', p.title);
    setText(root, '[data-lp-price]', p.price);
    setText(root, '[data-lp-why]', rec.why);
    var rated = typeof p.rating === 'number';
    $$('[data-lp-rating-row]', root).forEach(function (n) { n.hidden = !rated; });
    if (rated) {
      var reviews = p.count + (p.count === 1 ? ' review' : ' reviews');
      setText(root, '[data-lp-rating]', p.rating.toFixed(1) + ' out of 5');
      setText(root, '[data-lp-rating-short]', p.rating.toFixed(1) + ' / 5');
      setText(root, '[data-lp-reviews]', reviews);
      setText(root, '[data-lp-rating-line]', p.rating.toFixed(1) + ' out of 5 from ' + reviews);
    }
    setText(root, '[data-lp-count]', String(Object.keys(answers || {}).length));
    $$('img[data-lp-img]', root).forEach(function (img) {
      if (!p.image) { img.hidden = true; return; }
      img.hidden = false;
      img.setAttribute('srcset', p.image.srcset);
      img.setAttribute('src', p.image.src);
      img.setAttribute('alt', p.image.alt);
    });
    $$('a[data-lp-learn]', root).forEach(function (a) { a.setAttribute('href', p.url); });
    $$('[data-lp-benefits]', root).forEach(function (list) {
      list.innerHTML = '';
      rec.benefits.forEach(function (b) {
        var li = document.createElement('li');
        li.className = list.getAttribute('data-lp-benefits');
        li.insertAdjacentHTML('beforeend', BENEFIT_SVG);
        var span = document.createElement('span');
        span.textContent = b;
        li.appendChild(span);
        list.appendChild(li);
      });
    });
    $$('[data-lp-pair]', root).forEach(function (box) {
      box.hidden = !rec.pair;
      if (rec.pair) {
        setText(box, '[data-lp-pair-name]', rec.pair);
        setText(box, '[data-lp-pair-why]', rec.pairWhy);
      }
    });
  }

  function initResult(root) {
    var card = $('[data-lp-card]', root);
    var browse = $('[data-lp-browse-state]', root);
    var sticky = $('[data-lp-sticky]', root);
    var spacer = $('[data-lp-sticky-spacer]', root);
    var watch = null;

    /* The spacer reserves the bar's height at the very end of the page, so
       the fixed bar can never cover the footer's last links. */
    if (spacer) {
      var tail = document.createElement('div');
      tail.className = 'lp';
      tail.appendChild(spacer);
      document.body.appendChild(tail);
    }

    function stickyWatch() {
      if (!sticky || watch || typeof window.IntersectionObserver !== 'function') return;
      var seen = new Set();
      watch = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) seen.add(e.target); else seen.delete(e.target);
        });
        var down = seen.size > 0;
        sticky.classList.toggle('is-stood-down', down);
        sticky.inert = down;
        sticky.setAttribute('aria-hidden', down ? 'true' : 'false');
      }, { threshold: 0.01 });
      $$('.lp-btn-primary, .lp-offer, .lp-steps__head-link').forEach(function (n) {
        if (!sticky.contains(n)) watch.observe(n);
      });
    }

    function measure() {
      if (!sticky || sticky.hidden) return;
      var h = Math.ceil(sticky.getBoundingClientRect().height);
      if (h > 0) spacer.style.height = h + 'px';
    }

    function show(rec, answers, meta) {
      var product = hasProduct(rec);
      root.hidden = !rec && !DESIGN;
      if (card) card.hidden = !product && !(DESIGN && !rec);
      if (browse) browse.hidden = !(rec && rec.action === 'browse');
      if (product) paintProduct(root, rec, answers);
      if (sticky) {
        sticky.hidden = !product;
        spacer.hidden = !product;
        if (product) {
          measure();
          window.requestAnimationFrame(function () {
            sticky.classList.add('is-shown');
            stickyWatch();
          });
        } else {
          sticky.classList.remove('is-shown', 'is-stood-down');
        }
      }
      if (!rec || !meta) return;
      if (meta.source === 'complete' || meta.source === 'refresh') {
        land(product ? $('[data-lp-head]', root) : $('[data-lp-browse-head]', root), root.hasAttribute('data-lp-smooth'));
      }
    }

    var reopen = $('[data-lp-reopen]', root);
    if (reopen) {
      reopen.addEventListener('click', function () {
        if (!quiz) return;
        quiz.reopen();
        quiz.focusCurrent();
      });
    }
    window.addEventListener('resize', measure);

    on('results', function (d) { show(d.rec, d.answers, d.meta); });
    on('reset', function () { show(null); });

    if (state.rec) show(state.rec, state.answers, null);
    else if (DESIGN && state.config) {
      var preview = state.config.rules.filter(function (r) { return r.product; })[0];
      if (preview) paintProduct(root, preview, {});
      show(null);
    } else show(null);
  }

  /* ------------------------------------------------------------------
     The intro tag on the quiz section fills in with the match.
     ------------------------------------------------------------------ */

  function initIntroTag(tag) {
    function paint(rec) {
      var filled = hasProduct(rec);
      tag.setAttribute('data-filled', filled ? '1' : '0');
      if (filled) paintProduct(tag, rec, state.answers);
      else setText(tag, '[data-lp-name], [data-lp-price], [data-lp-rating-short]', '');
    }
    on('results', function (d) { paint(d.rec); });
    on('reset', function () { paint(null); });
    paint(state.rec);
  }

  /* A key is "result=<rule id>" for the recommendation or
     "<question id>=<value>" for an answer. */
  function keyMatches(key, rec, answers) {
    if (!rec) return false;
    var k = key.split('=');
    var q = k[0].trim();
    var v = (k[1] || '').trim();
    return q === 'result' ? rec.id === v : answers[q] === v;
  }

  /* ------------------------------------------------------------------
     Placement figure: a default image, swapped for the variant whose key
     matches the recommendation or an answer.
     ------------------------------------------------------------------ */

  function initPlacement(root) {
    var data;
    try { data = JSON.parse($('script[data-lp-placement]', root).textContent); } catch (e) { return; }
    var frame = $('[data-lp-place-frame]', root);
    var img = frame ? $('img', frame) : null;
    var fallback = img ? {
      src: img.getAttribute('src'),
      srcset: img.getAttribute('srcset') || '',
      alt: img.getAttribute('alt'),
      w: img.getAttribute('width'),
      h: img.getAttribute('height')
    } : null;
    var texts = {};
    $$('[data-lp-place]', root).forEach(function (n) { texts[n.getAttribute('data-lp-place')] = n.textContent; });

    var links = $$('[data-lp-place-link]', root).map(function (n) {
      return { node: n, href: n.getAttribute('href'), label: n.textContent };
    });

    function pick(rec, answers, test) {
      for (var i = 0; i < data.length; i++) {
        if (test(data[i]) && keyMatches(data[i].key, rec, answers)) return data[i];
      }
      return null;
    }

    function hasContent(d) { return !!(d.image || d.heading || d.caption || d.text); }
    function hasLink(d) { return !!d.link_url; }

    function paint(rec, answers) {
      var v = pick(rec, answers || {}, hasContent);
      var l = pick(rec, answers || {}, hasLink);
      links.forEach(function (x) {
        x.node.setAttribute('href', l ? l.link_url : x.href);
        x.node.textContent = l && l.link_label ? l.link_label : x.label;
      });
      $$('[data-lp-place]', root).forEach(function (n) {
        var f = n.getAttribute('data-lp-place');
        n.textContent = v && v[f] ? v[f] : texts[f];
      });
      var src = v && v.image ? v.image : fallback;
      if (!frame || !src || !src.src) return;
      if (!img) {
        img = document.createElement('img');
        img.setAttribute('decoding', 'async');
        frame.appendChild(img);
      }
      if (src.srcset) img.setAttribute('srcset', src.srcset);
      else img.removeAttribute('srcset');
      img.setAttribute('src', src.src);
      img.setAttribute('alt', src.alt || '');
      if (src.w) { img.setAttribute('width', src.w); img.setAttribute('height', src.h); }
    }

    on('results', function (d) { paint(d.rec, d.answers); });
    on('reset', function () { paint(null); });
    paint(state.rec, state.answers);
  }

  /* ------------------------------------------------------------------
     Reviews: one with a data-lp-review key shows only while that key
     matches; one without always shows. The section hides when none can.
     ------------------------------------------------------------------ */

  function initReviews(root) {
    var keyed = $$('[data-lp-review]', root);
    if (!keyed.length || DESIGN) return;
    var always = $$('.lp-cardrow > *', root).length - keyed.length;

    function paint(rec, answers) {
      var shown = always;
      keyed.forEach(function (n) {
        var ok = keyMatches(n.getAttribute('data-lp-review'), rec, answers || {});
        n.hidden = !ok;
        if (ok) shown++;
      });
      root.hidden = shown === 0;
    }

    on('results', function (d) { paint(d.rec, d.answers); });
    on('reset', function () { paint(null); });
    paint(state.rec, state.answers);
  }

  /* ------------------------------------------------------------------
     Email forms. data-lp-form="gate" unlocks the offer and refuses until
     the quiz is complete; "capture" just records the address. Nothing is
     sent unless the section has a Klaviyo company id and list id.
     ------------------------------------------------------------------ */

  function subscribe(form, lead) {
    var company = form.getAttribute('data-klaviyo-company');
    var list = form.getAttribute('data-klaviyo-list');
    if (!company || !list) return;
    var props = {};
    var a = state.answers || {};
    for (var k in a) if (Object.prototype.hasOwnProperty.call(a, k)) props['lp_' + k] = a[k];
    if (state.rec && state.rec.product) props.lp_recommended = state.rec.product.handle;
    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: form.getAttribute('data-lp-source') || 'Landing page',
            profile: { data: { type: 'profile', attributes: { email: lead.email, first_name: lead.firstName || undefined, properties: props } } }
          },
          relationships: { list: { data: { type: 'list', id: list } } }
        }
      })
    }).catch(function () {});
  }

  function validate(form) {
    var ok = true;
    $$('.lp-field', form).forEach(function (f) {
      var input = $('.lp-field__input', f);
      if (!input) return;
      var v = (input.value || '').trim();
      var bad = !v || (input.type === 'email' && !EMAIL_RE.test(v));
      f.classList.toggle('is-invalid', bad);
      input.setAttribute('aria-invalid', bad ? 'true' : 'false');
      if (bad && ok) { input.focus(); ok = false; }
    });
    return ok;
  }

  function initForm(root) {
    var form = $('form[data-lp-form]', root);
    if (!form) return;
    var mode = form.getAttribute('data-lp-form');
    var status = $('[data-lp-status]', root);
    var actions = $('[data-lp-form-actions]', root);

    function sync() {
      if (mode !== 'gate') return;
      var passed = state.gatePassed && hasProduct(state.rec);
      if (actions) actions.hidden = passed;
      if (status) {
        status.textContent = passed ? status.getAttribute('data-done')
          : (state.rec ? '' : status.getAttribute('data-pending'));
      }
    }

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      if (!validate(form)) return;
      var name = $('[name="first_name"]', form);
      var lead = { firstName: name ? name.value.trim() : '', email: $('[name="email"]', form).value.trim() };
      if (mode === 'gate') {
        if (!quiz || !quiz.isComplete() || quiz.gatePassed()) return;
        track('Lead', { content_name: 'gate' });
        subscribe(form, lead);
        quiz.submitGate(lead);
        return;
      }
      track('Lead', { content_name: 'email_capture' });
      subscribe(form, lead);
      if (status) status.textContent = status.getAttribute('data-done');
    });

    on('results', sync);
    on('reset', sync);
    sync();
  }

  /* ------------------------------------------------------------------
     The offer the gate unlocks lands the visitor on itself.
     ------------------------------------------------------------------ */

  function initOffer(root) {
    var flag = $('[data-lp-flag]', root);
    function sync(d) {
      var open = state.gatePassed && hasProduct(state.rec);
      if (flag) flag.classList.toggle('is-set', open || DESIGN);
      if (open && d && d.meta && d.meta.source === 'gate') {
        land(root, false);
        var buy = $('[data-lp-buy]', root);
        if (buy) buy.focus({ preventScroll: true });
      }
    }
    on('results', sync);
    on('reset', sync);
    sync();
  }

  /* ------------------------------------------------------------------
     Session countdown. The clock is information: it never disables a
     control and at zero it says so rather than withdrawing anything.
     ------------------------------------------------------------------ */

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  function initCountdown(node) {
    if (node.getAttribute('data-lp-ticking')) return;
    node.setAttribute('data-lp-ticking', '1');
    var key = node.getAttribute('data-lp-countdown');
    var ms = parseFloat(node.getAttribute('data-hours') || '48') * 3600000;
    var anchor = parseInt(store('sessionStorage', key) || '', 10);
    if (!anchor || isNaN(anchor)) {
      anchor = Date.now();
      store('sessionStorage', key, String(anchor));
    }
    var deadline = anchor + ms;
    var timer = null;
    function tick() {
      var left = deadline - Date.now();
      if (left <= 0) {
        node.textContent = node.getAttribute('data-expired') || '';
        if (timer) window.clearInterval(timer);
        return;
      }
      var t = Math.floor(left / 1000);
      node.textContent = pad(Math.floor(t / 3600)) + ':' + pad(Math.floor((t % 3600) / 60)) + ':' + pad(t % 60);
    }
    tick();
    timer = window.setInterval(tick, 1000);
  }

  /* ------------------------------------------------------------------
     Ambient reveal and the stepper. Transform and opacity only; reduced
     motion shows everything at once; a safety net means the motion layer
     can never leave anything unreadable.
     ------------------------------------------------------------------ */

  function observeOnce(nodes, cls, options, netMs) {
    if (!nodes.length) return;
    function all() { nodes.forEach(function (n) { n.classList.add(cls); }); }
    if (reducedMotion() || DESIGN || typeof window.IntersectionObserver !== 'function') { all(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add(cls);
        io.unobserve(e.target);
      });
    }, options);
    nodes.forEach(function (n) { io.observe(n); });
    window.setTimeout(function () { all(); io.disconnect(); }, netMs);
  }

  /* ------------------------------------------------------------------
     Cart. Same route as the Vector board finder: /cart/add.js, then the
     UpCart drawer when UpCart owns the cart, the theme drawer otherwise,
     and /cart when neither is mounted.
     ------------------------------------------------------------------ */

  function openCart(result, host) {
    var rendered = false;
    var opened = false;
    if (document.body && document.body.hasAttribute('data-upcart-cart-enabled')) {
      if (typeof window.upcartOpenCart === 'function') {
        try { window.upcartOpenCart(); opened = true; } catch (e) { /* UpCart not mounted yet */ }
      }
    } else if (host) {
      opened = true;
      try {
        host.classList.remove('is-empty');
        if (typeof host.renderContents === 'function' && result && result.sections) {
          host.renderContents(result);
          rendered = true;
        }
      } catch (e) { /* the drawer is an enhancement */ }
      if (typeof window.publish === 'function' && typeof window.PUB_SUB_EVENTS === 'object' && window.PUB_SUB_EVENTS.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'lp', cartData: result });
      }
    }
    if (!rendered) {
      fetch(window.location.pathname + '?section_id=cart-icon-bubble')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var bubble = document.getElementById('cart-icon-bubble');
          if (!bubble) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var next = doc.querySelector('.shopify-section') || doc.body;
          if (next) bubble.innerHTML = next.innerHTML;
        })
        .catch(function () {});
    }
    return opened;
  }

  var adding = false;

  function addToCart(variantId, trigger, discount) {
    if (adding || !variantId) return;
    adding = true;
    var label = trigger.innerHTML;
    trigger.disabled = true;
    trigger.textContent = 'Adding...';
    var host = document.body.hasAttribute('data-upcart-cart-enabled') ? null
      : (document.querySelector('cart-notification') || document.querySelector('cart-drawer'));
    var payload = { items: [{ id: variantId, quantity: 1 }] };
    if (host && typeof host.getSectionsToRender === 'function') {
      try {
        payload.sections = host.getSectionsToRender().map(function (s) { return s.id; });
        payload.sections_url = window.location.pathname;
        if (typeof host.setActiveElement === 'function') host.setActiveElement(trigger);
      } catch (e) { /* sections are an enhancement */ }
    }
    var pre = discount ? fetch('/discount/' + encodeURIComponent(discount), { credentials: 'same-origin' }).catch(function () {}) : Promise.resolve();
    var message = 'Added';
    pre.then(function () {
      return fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
    }).then(function (res) {
      return res.json().catch(function () { return null; }).then(function (json) {
        if (!res.ok || (json && json.status)) throw new Error((json && (json.description || json.message)) || 'Could not add, try again');
        track('AddToCart', { variant_id: variantId });
        // with no drawer mounted (the live default) the PDP goes to /cart too
        if (!openCart(json, host)) window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      });
    }).catch(function (err) {
      message = err && err.message ? err.message : 'Could not add, try again';
    }).then(function () {
      trigger.textContent = message;
      window.setTimeout(function () {
        trigger.innerHTML = label;
        trigger.disabled = false;
        adding = false;
      }, 1600);
    });
  }

  /* ------------------------------------------------------------------
     Delegated controls.
     ------------------------------------------------------------------ */

  document.addEventListener('click', function (ev) {
    var t = ev.target.closest ? ev.target.closest('[data-lp-buy], [data-lp-add], [data-lp-start], a[data-lp-browse]') : null;
    if (!t || !t.closest('.lp')) return;

    if (t.hasAttribute('data-lp-start')) {
      if (!quiz) return;
      quiz.markStart();
      /* The theme header is sticky, so scrollIntoView would park the quiz's
         first row under it. Land just below the header instead. */
      var head = $('.section-header');
      var cover = head && /^(sticky|fixed)$/.test(window.getComputedStyle(head).position) ? head.getBoundingClientRect().height : 0;
      window.scrollTo(0, Math.max(0, quiz.root.getBoundingClientRect().top + window.pageYOffset - cover - 16));
      quiz.focusCurrent();
      return;
    }
    if (t.hasAttribute('data-lp-add')) {
      addToCart(parseInt(t.getAttribute('data-lp-add'), 10), t, null);
      return;
    }
    if (t.hasAttribute('data-lp-buy')) {
      if (!hasProduct(state.rec)) return;
      track('InitiateCheckout', { content_name: t.getAttribute('data-lp-buy'), content_ids: [state.rec.product.handle] });
      addToCart(state.rec.product.variant_id, t, state.config && state.config.discountCode);
      return;
    }
    track('ViewContent', { content_name: t.getAttribute('data-lp-browse') });
    t.setAttribute('href', withUtms(t.getAttribute('href')));
  });

  /* ------------------------------------------------------------------
     Boot. Listeners attach first and the quiz starts last, because a
     reload with finished answers publishes results synchronously.
     ------------------------------------------------------------------ */

  function initRoot(scope) {
    $$('[data-lp="result"]', scope).forEach(initResult);
    $$('[data-lp-intro-tag]', scope).forEach(initIntroTag);
    $$('[data-lp="placement"]', scope).forEach(initPlacement);
    $$('[data-lp="reviews"]', scope).forEach(initReviews);
    $$('[data-lp="form"]', scope).forEach(initForm);
    $$('[data-lp="offer"]', scope).forEach(initOffer);
    $$('[data-lp-countdown]', scope).forEach(initCountdown);
    /* One yellow mark per view: the hero clock hands its mark to the result
       card once there is one. */
    $$('[data-lp-mark-handoff]', scope).forEach(function (m) {
      function sync() { m.classList.toggle('lp-tag--mark', !hasProduct(state.rec)); }
      on('results', sync);
      on('reset', sync);
      sync();
    });
    observeOnce($$('.lp-reveal', scope), 'seen', { rootMargin: '0px 0px -10% 0px', threshold: 0.01 }, 1600);
    observeOnce($$('.lp-step', scope), 'is-on', { rootMargin: '0px 0px -18% 0px', threshold: 0.3 }, 6000);
    applyReveals(scope);

    var q = $('[data-lp="quiz"]', scope);
    if (q) {
      var cfg;
      try { cfg = JSON.parse($('script[data-lp-config]', q).textContent); } catch (e) { return; }
      state.config = cfg;
      quiz = createQuiz(q, cfg);
      track('PageView', {});
      quiz.start();
    }
  }

  window.LP = { state: state, on: on, track: track, get quiz() { return quiz; } };

  function boot() {
    /* The config has to be known before a result card can show its editor
       preview, so it is read ahead of the first pass. */
    var cfgNode = $('[data-lp="quiz"] script[data-lp-config]');
    if (cfgNode) { try { state.config = JSON.parse(cfgNode.textContent); } catch (e) { /* bad config */ } }
    initRoot(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', function (ev) { initRoot(ev.target); });
})();
